import { Injectable } from '@angular/core';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';
import { CrewDetail, MemberDetail } from '../model/crew-detail';
import { environment } from '@environments/environment';
import { ApiCacheService } from '@shared/services/cache-service/api-cache.service';
import { CrewMember } from '../model/crew-member';
import { SignalsService } from '@shared/services/signals/signals.service';
import { HttpClient } from '@angular/common/http';
import { DateTime } from 'luxon';
import { MessageService } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
export class FlightdetailService {

  private readonly CACHE_EXPIRATION_MINUTES = 30; // Time before refresh.

  /** Employee number making the request for
   *  an employee contact details
   * */
  intiatorEmpNum$ = this.signalsService.employeeNumber$;

  /** Employee number being requestedto get 
  * contact details for 
  */
  _requestedEmpNum$ = new BehaviorSubject<string>('')
  requestedEmpNum$ = this._requestedEmpNum$.asObservable();


  constructor(
    private cacheService: ApiCacheService,
    private signalsService: SignalsService,
    private http: HttpClient,
    private messageService: MessageService,
  ) { }

  private readonly _crewBasicUrl = `${environment.crewBasicUrl}`


  // Crew User API service (crew basic info like name, emp number, etc)
  getCrewDetailsFor(empNumber: string[], cacheId: string): Observable<RequestState<CrewDetail[]>> {
    const url = `${this._crewBasicUrl}`

    return this.cacheService.request<MemberDetail>(cacheId, url, 'memberDetails', empNumber)
      .pipe(
        map((crewDetailResponse) => {
          if (!crewDetailResponse || !crewDetailResponse.response) {
            return new RequestState<CrewDetail[]>(
              RequestStatus.IsCompleteNoResponse
            )
          }
          let crewDetail: CrewDetail[] = [];
          if (crewDetailResponse.response) {
            for (let member of crewDetailResponse.response.memberDetails) {
              crewDetail.push({
                username: member.username,
                firstName: member.firstName,
                lastName: member.lastName,
                middleName: member.middleName,
                nickName: member.nickName,
                seat: member.seat
              })
            }
          }
          return new RequestState<CrewDetail[]>(
            RequestStatus.IsCompleteWithResponse,
            crewDetail
          )
        })
      )
  }

  // getCrewMemberDetails(requestedEmpNum: string, initiatorEmpNum: string, cacheId: string): Observable<RequestState<CrewMember>> {
  //   const url = `${environment.crewDetailUrl}`;

  //   const body = {
  //     searchTerm: requestedEmpNum, //empNumber to search
  //     userId: initiatorEmpNum, //empNumber conducting the search
  //     request: 0
  //   }

  //   return this.cacheService.request<CrewMember>(cacheId, url, undefined, body)
  //     .pipe(
  //       map((response) => {
  //         if ((!response.response || response.hasError) && !response.isLoading) {
  //           console.error('No response from server flight detail service');
  //           return new RequestState<CrewMember>(
  //             RequestStatus.IsCompleteNoResponse
  //           )
  //         }

  //         return new RequestState<CrewMember>(
  //           RequestStatus.IsCompleteWithResponse,
  //           response.response
  //         )
  //       })
  //     )
  // }

  /**
   * Use this mehtod to get crew member detail using post request instead of the cache service.
   * This makes the skeleton loading work properly and not using the loading modal.
   * @param requestedEmpNum - the employee number of the crew member being requested
   * @param initiatorEmpNum - the employee number of the user making the request
   * @param cacheId - a unique identifier for caching the response
   * @returns - an observable of RequestState containing CrewMember contact details
   */
  getCrewMemberDetails(requestedEmpNum: string, initiatorEmpNum: string, cacheId: string): Observable<RequestState<CrewMember>> {
    const url = `${environment.crewDetailUrl}`;

    const body = {
      searchTerm: requestedEmpNum, //empNumber to search
      userId: initiatorEmpNum, //empNumber conducting the search
      request: 0
    }
    const cachedResponse = this.getCached<CrewMember>(cacheId);
    if (cachedResponse) {
      if (
        cachedResponse.timestamp &&
        DateTime.fromISO(cachedResponse.timestamp, { zone: 'utc' }).diffNow(
          'minutes'
        ).minutes *
        -1 <=
        this.CACHE_EXPIRATION_MINUTES
      ) {
        return of(
          new RequestState<CrewMember>(
            RequestStatus.IsCompleteWithResponse,
            cachedResponse
          )
        );
      }
    }

    return this.http.post<CrewMember>(url, body).pipe(
      map((response) => {
        if (response) {
          this.cacheResponse(cacheId, response);
        }

        return new RequestState<CrewMember>(
          RequestStatus.IsCompleteWithResponse,
          response
        );
      }),
      catchError((error) => {
        console.error('Error fetching crew member details:', error);
        const cachedResponse = this.getCached<CrewMember>(cacheId);
        if (cachedResponse) {
          return of(
            new RequestState<CrewMember>(
              RequestStatus.IsCompleteWithResponse,
              cachedResponse
            )
          );
        }
        this.messageService.add({severity: 'error', summary: 'Error', detail: 'Failed to fetch crew member contact details.'});
        return of(
          new RequestState<CrewMember>(
            RequestStatus.IsCompleteNoResponse
          )
        );
      })
    );
  }

  cacheResponse<T>(
    cacheId: string,
    response: T
  ): void {
    localStorage.setItem(cacheId, JSON.stringify(response));
  }

  private getCached<T>(cacheId: string): T | null {
    const cached = localStorage.getItem(cacheId);

    if (!cached) {
      return null;
    }

    return JSON.parse(cached) as T;
  }


}
